import {
  UserManager,
  WebStorageStateStore,
  type OidcMetadata,
  type UserManagerSettings,
} from 'oidc-client-ts';

import { getConfig } from '@/config';
import { SplitTokenStore } from '@/auth/token-store';

/** Where Cognito sends the browser back after the authorization code hop. */
export const CALLBACK_PATH = '/auth/callback';

/** The hidden iframe document used when no refresh token is available. */
export const SILENT_RENEW_PATH = '/silent-renew.html';

/** Where the Managed Login logout endpoint returns to. Must be registered as a
 *  sign out URL on the app client. */
export const POST_LOGOUT_PATH = '/';

/**
 * Cognito's OIDC discovery document is not a safe thing to depend on here. It
 * omits `end_session_endpoint` entirely, and the endpoints it advertises point
 * at the pool's generated `*.auth.<region>.amazoncognito.com` domain rather
 * than at the branded `auth.engage.<domain>` host that plan 8.1.1 requires.
 * Supplying metadata explicitly removes the discovery round trip from every
 * cold load and makes the branded domain authoritative.
 *
 * `jwks_uri` still points at the issuer, which is where Cognito actually serves
 * the signing keys.
 *
 * `end_session_endpoint` is deliberately absent: Cognito's `/logout` is not
 * RP initiated OIDC logout, it takes `client_id` plus `logout_uri`. `signOut()`
 * below builds that URL directly rather than letting the library construct a
 * standards shaped request that Cognito would reject.
 */
function cognitoMetadata(authority: string, domain: string): Partial<OidcMetadata> {
  return {
    issuer: authority,
    authorization_endpoint: `${domain}/oauth2/authorize`,
    token_endpoint: `${domain}/oauth2/token`,
    userinfo_endpoint: `${domain}/oauth2/userInfo`,
    revocation_endpoint: `${domain}/oauth2/revoke`,
    jwks_uri: `${authority}/.well-known/jwks.json`,
  };
}

export function buildSettings(origin: string): UserManagerSettings {
  const config = getConfig();

  return {
    authority: config.authAuthority,
    metadata: cognitoMetadata(config.authAuthority, config.authDomain),
    client_id: config.authClientId,
    redirect_uri: `${origin}${CALLBACK_PATH}`,
    silent_redirect_uri: `${origin}${SILENT_RENEW_PATH}`,
    post_logout_redirect_uri: `${origin}${POST_LOGOUT_PATH}`,

    // Authorization code with PKCE S256. `oidc-client-ts` uses S256 whenever
    // PKCE is enabled, and PKCE is only disabled by `disablePKCE`, which is
    // never set here. Plan D20 and 8.4.2.
    response_type: 'code',
    scope: 'openid email profile',

    // Access and ID tokens in memory, refresh token in sessionStorage.
    userStore: new SplitTokenStore(),
    // PKCE verifiers and signin state must survive the full page navigation to
    // the hosted UI, so they cannot live in memory. Prefixed because
    // `clearStaleState` deletes any key in this store it cannot parse.
    stateStore: new WebStorageStateStore({
      store: window.sessionStorage,
      prefix: 'zs.oidc.state.',
    }),

    automaticSilentRenew: true,
    validateSubOnSilentRenew: true,
    // Cognito ID and access tokens last 60 minutes. Renewing a full minute
    // early keeps a long lived dashboard tab from ever issuing a request with a
    // token that expires mid flight.
    accessTokenExpiringNotificationTimeInSeconds: 60,
    // Two failed iframe renews in a row means third party storage is
    // partitioned or the session is gone. Stop retrying and let the app fall
    // back to an interactive sign in rather than looping invisibly.
    maxSilentRenewTimeoutRetries: 2,

    // Session monitoring needs the OP iframe, which Cognito does not provide.
    monitorSession: false,
    // The ID token is the read API credential (plan 5.10), so a userinfo hop on
    // every sign in buys nothing and costs a round trip.
    loadUserInfo: false,
  };
}

let manager: UserManager | null = null;

/** One `UserManager` per document. The silent renew iframe builds its own. */
export function getUserManager(): UserManager {
  if (manager === null) manager = new UserManager(buildSettings(window.location.origin));
  return manager;
}

/**
 * Cognito's sign out endpoint. It is not `end_session_endpoint` and it does not
 * accept `id_token_hint`; the documented shape is `client_id` plus `logout_uri`,
 * and `logout_uri` must exactly match a sign out URL registered on the app
 * client.
 */
export function buildLogoutUrl(origin: string): string {
  const config = getConfig();
  const params = new URLSearchParams({
    client_id: config.authClientId,
    logout_uri: `${origin}${POST_LOGOUT_PATH}`,
  });
  return `${config.authDomain}/logout?${params.toString()}`;
}
